import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  ChevronLeft,
  Calendar,
  Gauge,
  Fuel,
  Settings,
  Car,
  Package,
  MapPin,
  Mail,
  Phone,
  ChevronRight,
  ChevronLeft as ChevronLeftIcon,
  X,
  Share2,
  Printer,
  Heart,
  GitCompare,
  Lock,
  Camera,
  ClipboardCheck,
  Hash
} from 'lucide-react';
import PublicHeader from './PublicHeader';
import PublicFooter from './PublicFooter';
import InquiryForm from './InquiryForm';
import ScrollToTop from './ScrollToTop';
import WhatsAppButton from './WhatsAppButton';
import SEOHead from './SEOHead';
import { useVehicles } from '@/contexts/VehicleContext';
import ShippingCalculator from './ShippingCalculator';
import ReservationForm from './ReservationForm';
import RequestPhotosForm from './RequestPhotosForm';
import ScheduleInspectionForm from './ScheduleInspectionForm';

import { BACKEND_URL_EXPORT as BACKEND_URL, API } from '../../config';

const getImageUrl = (imagePath) => {
  if (!imagePath) return imagePath;
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  const vehicleImageMatch = imagePath.match(/\/uploads\/vehicle_images\/([^/]+)\/([^/]+)/);
  if (vehicleImageMatch) {
    return `${API}/images/${vehicleImageMatch[1]}/${vehicleImageMatch[2]}`;
  }
  const additionalImageMatch = imagePath.match(/\/uploads\/additional_images\/([^/]+)\/([^/]+)/);
  if (additionalImageMatch) {
    return `${API}/additional-images/${additionalImageMatch[1]}/${additionalImageMatch[2]}`;
  }
  return `${BACKEND_URL}${imagePath}`;
};

const VehicleDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showInquiryForm, setShowInquiryForm] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showReservationForm, setShowReservationForm] = useState(false);
  const [showRequestPhotos, setShowRequestPhotos] = useState(false);
  const [showScheduleInspection, setShowScheduleInspection] = useState(false);
  const { toggleFavorite, isFavorite, toggleCompare, isInCompare } = useVehicles();

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchVehicleDetail();
  }, [id]);

  // Close share menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showShareMenu && !event.target.closest('.share-menu-container')) {
        setShowShareMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showShareMenu]);

  const fetchVehicleDetail = async () => {
    try {
      const response = await axios.get(`${API}/public/vehicles/${id}`);
      setVehicle(response.data);
    } catch (error) {
      console.error('Error fetching vehicle:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <PublicHeader />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="animate-pulse space-y-6">
            <div className="h-96 bg-gray-200 rounded-lg"></div>
            <div className="h-32 bg-gray-200 rounded-lg"></div>
            <div className="h-64 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="min-h-screen bg-white">
        <PublicHeader />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <Car className="h-24 w-24 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-700 mb-2">Vehicle Not Found</h2>
          <p className="text-gray-500 mb-6">The vehicle you're looking for is not available.</p>
          <Button onClick={() => navigate('/vehicles')}>
            Back to Vehicles
          </Button>
        </div>
      </div>
    );
  }

  const allImages = vehicle.images && vehicle.images.length > 0 
    ? vehicle.images.filter(img => img && img !== '')
    : [];
  
  const hasImages = allImages.length > 0;
  const currentImage = hasImages ? allImages[selectedImageIndex] : null;

  const formatPrice = (priceUsd) => {
    if (priceUsd) {
      return `$${priceUsd.toLocaleString()}`;
    }
    return 'Contact for Price';
  };

  const nextImage = () => {
    if (hasImages) {
      setSelectedImageIndex((prev) => (prev + 1) % allImages.length);
    }
  };

  const prevImage = () => {
    if (hasImages) {
      setSelectedImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
    }
  };

  const specs = [
    { key: 'stock_no', label: 'Stock No', value: vehicle.stock_no || 'N/A', icon: Hash },
    { key: 'year', label: 'Year', value: vehicle.year, icon: Calendar },
    { key: 'mileage', label: 'Mileage', value: vehicle.mileage ? `${vehicle.mileage.toLocaleString()} km` : 'N/A', icon: Gauge },
    { key: 'fuel', label: 'Fuel Type', value: vehicle.fuel_type || 'N/A', icon: Fuel },
    { key: 'transmission', label: 'Transmission', value: vehicle.transmission || 'N/A', icon: Settings },
    { key: 'body', label: 'Body Type', value: vehicle.body_type || 'N/A', icon: Car },
    { key: 'engine', label: 'Engine', value: vehicle.engine_cc ? `${vehicle.engine_cc} cc` : 'N/A', icon: Package },
    { key: 'dimension', label: 'Car Dimension (m3)', value: vehicle.freight_m3_unit || vehicle.freight_m3_unit === 0 ? `${vehicle.freight_m3_unit} m3` : 'N/A', icon: Package },
    { key: 'drive', label: 'Drive Type', value: vehicle.drive_type || 'N/A', icon: Settings },
    { key: 'color', label: 'Color', value: vehicle.color || 'N/A', icon: Package },
    { key: 'doors', label: 'Doors', value: vehicle.doors || 'N/A', icon: Car },
    { key: 'seats', label: 'Seats', value: vehicle.seats || 'N/A', icon: Car },
  ];

  const handlePrint = () => {
    window.print();
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const title = `${vehicle.year} ${vehicle.brand || ''} ${vehicle.model}`;
    const text = `Check out this ${title} at Global Trade Car!`;
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`,
      email: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(text + '\n\n' + url)}`
    };
    
    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'noopener,noreferrer');
      setShowShareMenu(false);
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${vehicle.year} ${vehicle.brand || ''} ${vehicle.model}`,
          text: `Check out this vehicle at Global Trade Car!`,
          url: window.location.href,
        });
        setShowShareMenu(false);
      } catch (err) {
        console.log('Share cancelled');
      }
    }
  };

  const vehicleStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: `${vehicle.year} ${vehicle.brand || ''} ${vehicle.model}`.trim(),
    description: vehicle.description || `${vehicle.year} ${vehicle.brand || ''} ${vehicle.model} for sale at Global Trade Car.`,
    image: hasImages ? getImageUrl(allImages[0]) : '/og-image.jpg',
    sku: vehicle.stock_no,
    brand: vehicle.brand ? { '@type': 'Brand', name: vehicle.brand } : undefined,
    ...(vehicle.public_price_usd
      ? {
          offers: {
            '@type': 'Offer',
            priceCurrency: 'USD',
            price: vehicle.public_price_usd,
            availability: 'https://schema.org/InStock',
            url: window.location.href,
            seller: { '@type': 'Organization', name: 'Global Trade Car' }
          }
        }
      : {}),
    additionalProperty: [
      ...(vehicle.year ? [{ '@type': 'PropertyValue', name: 'Year', value: vehicle.year }] : []),
      ...(vehicle.mileage ? [{ '@type': 'PropertyValue', name: 'Mileage', value: `${vehicle.mileage.toLocaleString()} km` }] : []),
      ...(vehicle.fuel_type ? [{ '@type': 'PropertyValue', name: 'Fuel Type', value: vehicle.fuel_type }] : []),
      ...(vehicle.transmission ? [{ '@type': 'PropertyValue', name: 'Transmission', value: vehicle.transmission }] : []),
      ...(vehicle.body_type ? [{ '@type': 'PropertyValue', name: 'Body Type', value: vehicle.body_type }] : []),
      ...(vehicle.engine_cc ? [{ '@type': 'PropertyValue', name: 'Engine', value: `${vehicle.engine_cc} cc` }] : []),
      ...(vehicle.drive_type ? [{ '@type': 'PropertyValue', name: 'Drive Type', value: vehicle.drive_type }] : []),
      ...(vehicle.color ? [{ '@type': 'PropertyValue', name: 'Color', value: vehicle.color }] : [])
    ]
  };

  return (
    <>
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print-area, .print-area * {
            visibility: visible;
          }
          .print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .no-print {
            display: none !important;
          }
          @page {
            margin: 1.5cm;
          }
        }
      `}</style>
      <div className="min-h-screen bg-white">
        <SEOHead 
          title={`${vehicle.year} ${vehicle.brand || ''} ${vehicle.model} - Global Trade Car`}
          description={`${vehicle.year} ${vehicle.brand || ''} ${vehicle.model} for sale. ${vehicle.mileage ? vehicle.mileage.toLocaleString() + ' km' : ''} ${vehicle.fuel_type || ''} ${vehicle.transmission || ''}. Contact us for details.`}
          ogImage={hasImages ? getImageUrl(allImages[0]) : '/og-image.jpg'}
          structuredData={vehicleStructuredData}
        />
        <div className="no-print">
          <PublicHeader />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <div className="no-print">
          <Button
            variant="ghost"
            onClick={() => navigate('/vehicles')}
            className="mb-4"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to Vehicles
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 print-area">
          {/* Left Column - Images & Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image Gallery */}
            <Card>
              <CardContent className="p-0">
                {/* Main Image */}
                <div className="relative h-[400px] bg-gray-100">
                  {hasImages ? (
                    <>
                      <img 
                        src={getImageUrl(currentImage)} 
                        alt={`${vehicle.year} ${vehicle.model}`}
                        className="w-full h-full object-contain cursor-zoom-in"
                        onClick={() => setShowImageModal(true)}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      {allImages.length > 1 && (
                        <>
                          <Button
                            variant="secondary"
                            size="icon"
                            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white"
                            onClick={prevImage}
                          >
                            <ChevronLeftIcon className="h-6 w-6" />
                          </Button>
                          <Button
                            variant="secondary"
                            size="icon"
                            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white"
                            onClick={nextImage}
                          >
                            <ChevronRight className="h-6 w-6" />
                          </Button>
                          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                            {selectedImageIndex + 1} / {allImages.length}
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full">
                      <Car className="h-32 w-32 text-white opacity-50" />
                      <Badge className="mt-4 bg-yellow-500 text-white">
                        Image Coming Soon
                      </Badge>
                    </div>
                  )}
                </div>

                {/* Thumbnail Gallery */}
                {hasImages && allImages.length > 1 && (
                  <div className="p-4 grid grid-cols-6 gap-2">
                    {allImages.map((img, idx) => (
                      <div
                        key={`thumb-${idx}`}
                        className={`relative h-16 cursor-pointer rounded overflow-hidden border-2 ${
                          idx === selectedImageIndex ? 'border-red-600' : 'border-gray-200'
                        }`}
                        onClick={() => setSelectedImageIndex(idx)}
                      >
                        <img 
                          src={getImageUrl(img)} 
                          alt={`Thumbnail ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Inspection Status (if available) */}
            {vehicle?.inspection_status && vehicle.inspection_status !== 'pending' && (
              <Card className="bg-gradient-to-r from-green-50 to-blue-50 border-green-200">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-3">
                    <div className={`rounded-full p-2 ${vehicle.inspection_status === 'pass' ? 'bg-green-500' : 'bg-yellow-500'}`}>
                      <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {vehicle.inspection_status === 'pass' ? '✅ Inspection Passed' : '⚠️ Inspection Status'}
                      </h3>
                      <p className="text-sm text-gray-700">
                        {vehicle.inspection_status === 'pass' 
                          ? 'This vehicle has passed our comprehensive inspection process.'
                          : 'This vehicle is currently under inspection review.'}
                      </p>
                      {vehicle.inspection_date && (
                        <p className="text-xs text-gray-600 mt-2">
                          Inspected on: {new Date(vehicle.inspection_date).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Vehicle Information */}
            <Card>
              <CardHeader>
                <CardTitle>Vehicle Specifications</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {specs.map((spec) => {
                    const Icon = spec.icon;
                    return (
                      <div key={spec.key} className="flex items-start space-x-3">
                        <div className="p-2 bg-red-50 rounded-lg">
                          <Icon className="h-5 w-5 text-red-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">{spec.label}</p>
                          <p className="font-semibold text-sm">{spec.value}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Additional Info */}
                <div className="mt-6 pt-6 border-t space-y-2">
                  {vehicle.grade && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Grade:</span>
                      <span className="font-semibold">{vehicle.grade}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Description */}
            {vehicle.description && (
              <Card>
                <CardHeader>
                  <CardTitle>Description</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 whitespace-pre-line">{vehicle.description}</p>
                </CardContent>
              </Card>
            )}

            {/* Features */}
            {vehicle.features && (
              <Card>
                <CardHeader>
                  <CardTitle>Features</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {vehicle.features.split(',').map((feature, idx) => (
                      <Badge key={`feature-${idx}`} variant="outline" className="text-sm">
                        {feature.trim()}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Shipping Info */}
            {(vehicle.pol || vehicle.pod) && (
              <Card>
                <CardHeader>
                  <CardTitle>Shipping Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {vehicle.pol && (
                    <div className="flex items-center space-x-2">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <span className="text-sm">
                        <span className="text-gray-600">Port of Loading:</span>{' '}
                        <span className="font-semibold">{vehicle.pol}</span>
                      </span>
                    </div>
                  )}
                  {vehicle.pod && (
                    <div className="flex items-center space-x-2">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <span className="text-sm">
                        <span className="text-gray-600">Destination:</span>{' '}
                        <span className="font-semibold">{vehicle.pod}</span>
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Shipping Calculator */}
            <ShippingCalculator vehicle={vehicle} />
          </div>

          {/* Right Column - Price & Inquiry */}
          <div className="space-y-6">
            {/* Price Card - Sticky */}
            <Card className="lg:sticky lg:top-20">
              <CardHeader>
                <CardTitle className="text-2xl">
                  {vehicle.year} {vehicle.brand || ''} {vehicle.model}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Price */}
                <div>
                  <p className="text-sm text-gray-500 mb-1">Price</p>
                  <p className="text-3xl font-bold text-red-600">
                    {formatPrice(vehicle.public_price_usd)}
                  </p>
                </div>

                {/* Status */}
                <div className="flex items-center space-x-2">
                  <Badge className="bg-green-600">Available</Badge>
                  <span className="text-sm text-gray-600">In Stock</span>
                </div>

                {/* Actions */}
                <div className="space-y-3">
                  <Button 
                    size="lg" 
                    className="w-full bg-green-600 hover:bg-green-700"
                    onClick={() => setShowReservationForm(true)}
                  >
                    <Lock className="h-5 w-5 mr-2" />
                    Reserve This Vehicle
                  </Button>

                  <Button 
                    size="lg" 
                    variant="outline"
                    className="w-full"
                    onClick={() => setShowInquiryForm(true)}
                  >
                    <Mail className="h-5 w-5 mr-2" />
                    Inquire About This Vehicle
                  </Button>

                  <div className="grid grid-cols-2 gap-2">
                    <Button 
                      size="lg" 
                      variant="outline"
                      onClick={() => setShowRequestPhotos(true)}
                    >
                      <Camera className="h-5 w-5 mr-2" />
                      Request Photos
                    </Button>
                    <Button 
                      size="lg" 
                      variant="outline"
                      onClick={() => setShowScheduleInspection(true)}
                    >
                      <ClipboardCheck className="h-5 w-5 mr-2" />
                      Schedule Inspection
                    </Button>
                  </div>
                  
                  <a href="tel:+817092702242">
                    <Button size="lg" variant="outline" className="w-full">
                      <Phone className="h-5 w-5 mr-2" />
                      Call Us
                    </Button>
                  </a>

                  {/* Quick Actions Row */}
                  <div className="grid grid-cols-4 gap-2 pt-3 border-t">
                    <button
                      onClick={() => toggleFavorite(vehicle)}
                      className={`flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
                        isFavorite(vehicle.id)
                          ? 'bg-red-50 text-red-600'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      title={isFavorite(vehicle.id) ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      <Heart className={`h-5 w-5 ${isFavorite(vehicle.id) ? 'fill-current' : ''}`} />
                      <span className="text-xs mt-1">Save</span>
                    </button>
                    
                    <button
                      onClick={() => toggleCompare(vehicle)}
                      className={`flex flex-col items-center justify-center p-3 rounded-lg transition-all ${
                        isInCompare(vehicle.id)
                          ? 'bg-red-50 text-red-600'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      title={isInCompare(vehicle.id) ? 'Remove from comparison' : 'Add to comparison'}
                    >
                      <GitCompare className="h-5 w-5" />
                      <span className="text-xs mt-1">Compare</span>
                    </button>
                    
                    <div className="relative share-menu-container">
                      <button
                        onClick={() => setShowShareMenu(!showShareMenu)}
                        className="flex flex-col items-center justify-center p-3 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all w-full"
                        title="Share this vehicle"
                      >
                        <Share2 className="h-5 w-5" />
                        <span className="text-xs mt-1">Share</span>
                      </button>
                      
                      {/* Share Dropdown */}
                      {showShareMenu && (
                        <div className="absolute bottom-full left-0 mb-2 bg-white rounded-lg shadow-xl border p-2 z-10 w-48 share-menu-container">
                          <button
                            onClick={() => handleShare('facebook')}
                            className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded text-sm flex items-center space-x-2"
                          >
                            <span>📘</span>
                            <span>Facebook</span>
                          </button>
                          <button
                            onClick={() => handleShare('twitter')}
                            className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded text-sm flex items-center space-x-2"
                          >
                            <span>🐦</span>
                            <span>Twitter</span>
                          </button>
                          <button
                            onClick={() => handleShare('whatsapp')}
                            className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded text-sm flex items-center space-x-2"
                          >
                            <span>💬</span>
                            <span>WhatsApp</span>
                          </button>
                          <button
                            onClick={() => handleShare('email')}
                            className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded text-sm flex items-center space-x-2"
                          >
                            <span>✉️</span>
                            <span>Email</span>
                          </button>
                          {navigator.share && (
                            <button
                              onClick={handleNativeShare}
                              className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded text-sm flex items-center space-x-2 border-t mt-1 pt-2"
                            >
                              <Share2 className="h-4 w-4" />
                              <span>More Options</span>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <button
                      onClick={handlePrint}
                      className="flex flex-col items-center justify-center p-3 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all"
                      title="Print specification sheet"
                    >
                      <Printer className="h-5 w-5" />
                      <span className="text-xs mt-1">Print</span>
                    </button>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="pt-6 border-t space-y-3 text-sm">
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Mail className="h-4 w-4" />
                    <a href="mailto:sales@globaltradecar.com" className="hover:text-red-600">
                      sales@globaltradecar.com
                    </a>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Phone className="h-4 w-4" />
                    <a href="tel:+817092702242" className="hover:text-red-600">
                      +81 70-9270-2242
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Reservation Form Modal */}
      <div className="no-print">
        {showReservationForm && (
          <ReservationForm
            vehicle={vehicle}
            onClose={() => setShowReservationForm(false)}
          />
        )}
      </div>

      {/* Inquiry Form Modal */}
      <div className="no-print">
        {showInquiryForm && (
          <InquiryForm
            vehicle={vehicle}
            onClose={() => setShowInquiryForm(false)}
          />
        )}
      </div>

      {/* Image Modal */}
      <div className="no-print">
        {showImageModal && hasImages && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setShowImageModal(false)}
        >
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 text-white hover:bg-white/20"
            onClick={() => setShowImageModal(false)}
          >
            <X className="h-6 w-6" />
          </Button>
          <img 
            src={getImageUrl(currentImage)} 
            alt={`${vehicle.year} ${vehicle.model}`}
            className="max-w-full max-h-full object-contain"
          />
          {allImages.length > 1 && (
            <>
              <Button
                variant="secondary"
                size="icon"
                className="absolute left-4 top-1/2 -translate-y-1/2"
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
              >
                <ChevronLeftIcon className="h-6 w-6" />
              </Button>
              <Button
                variant="secondary"
                size="icon"
                className="absolute right-4 top-1/2 -translate-y-1/2"
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
              >
                <ChevronRight className="h-6 w-6" />
              </Button>
            </>
          )}
        </div>
        )}
      </div>

      <div className="no-print">
        <PublicFooter />
        <ScrollToTop />
        <WhatsAppButton />
      </div>

      {/* Request Photos Modal */}
      {showRequestPhotos && (
        <RequestPhotosForm
          vehicle={vehicle}
          onClose={() => setShowRequestPhotos(false)}
        />
      )}

      {/* Schedule Inspection Modal */}
      {showScheduleInspection && (
        <ScheduleInspectionForm
          vehicle={vehicle}
          onClose={() => setShowScheduleInspection(false)}
        />
      )}
    </div>
    </>
  );
};

export default VehicleDetailPage;
